"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type GeolocationPermissionState = "granted" | "denied" | "prompt";

export interface GeolocationPosition {
  lat: number;
  lon: number;
}

export interface GeolocationError {
  code: "PERMISSION_DENIED" | "POSITION_UNAVAILABLE" | "TIMEOUT" | "UNKNOWN";
  message: string;
}

interface UseGeolocationOptions {
  /** Whether to request location immediately on mount (default: false) */
  enableOnMount?: boolean;
  /** Timeout for geolocation request in milliseconds (default: 10000) */
  timeout?: number;
  /** Maximum age of cached position in milliseconds (default: 60000) */
  maximumAge?: number;
  /** Enable high accuracy mode (default: false) */
  enableHighAccuracy?: boolean;
}

interface UseGeolocationReturn {
  /** Current position if available */
  position: GeolocationPosition | null;
  /** Error if geolocation failed */
  error: GeolocationError | null;
  /** Whether geolocation is currently being fetched */
  isLoading: boolean;
  /** Current permission state */
  permissionState: GeolocationPermissionState | null;
  /** Request the user's location */
  requestLocation: () => void;
  /** Clear the current position and error */
  clear: () => void;
  /** Whether geolocation is supported by the browser */
  isSupported: boolean;
}

/**
 * Hook for browser geolocation functionality.
 * Provides position, permission state, and methods to request location.
 */
export function useGeolocation(
  options: UseGeolocationOptions = {},
): UseGeolocationReturn {
  const {
    enableOnMount = false,
    timeout = 10000,
    maximumAge = 60000,
    enableHighAccuracy = false,
  } = options;

  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionState, setPermissionState] =
    useState<GeolocationPermissionState | null>(null);

  const isSupported =
    typeof navigator !== "undefined" && "geolocation" in navigator;

  // Track if a geolocation request is in progress to prevent race conditions
  const isRequestingRef = useRef(false);

  // Check permission state on mount
  useEffect(() => {
    if (!isSupported) return;

    // Check if the Permissions API is available
    if ("permissions" in navigator) {
      let permissionStatus: PermissionStatus | null = null;

      const handlePermissionChange = () => {
        if (permissionStatus) {
          setPermissionState(
            permissionStatus.state as GeolocationPermissionState,
          );
        }
      };

      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          permissionStatus = result;
          setPermissionState(result.state as GeolocationPermissionState);

          // Listen for permission changes
          result.addEventListener("change", handlePermissionChange);
        })
        .catch(() => {
          // Permissions API not fully supported, state will be determined on request
          setPermissionState(null);
        });

      // Cleanup: remove the event listener on unmount
      return () => {
        if (permissionStatus) {
          permissionStatus.removeEventListener(
            "change",
            handlePermissionChange,
          );
        }
      };
    }
  }, [isSupported]);

  const requestLocation = useCallback(() => {
    if (!isSupported) {
      setError({
        code: "UNKNOWN",
        message: "Geolocation is not supported by this browser",
      });
      return;
    }

    // Prevent overlapping requests
    if (isRequestingRef.current) {
      return;
    }

    isRequestingRef.current = true;
    setIsLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        isRequestingRef.current = false;
        setPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
        setIsLoading(false);
        setPermissionState("granted");
      },
      (err) => {
        isRequestingRef.current = false;
        let errorCode: GeolocationError["code"];
        let message: string;

        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorCode = "PERMISSION_DENIED";
            message = "Location access was denied";
            setPermissionState("denied");
            break;
          case err.POSITION_UNAVAILABLE:
            errorCode = "POSITION_UNAVAILABLE";
            message = "Location information is unavailable";
            break;
          case err.TIMEOUT:
            errorCode = "TIMEOUT";
            message = "Location request timed out";
            break;
          default:
            errorCode = "UNKNOWN";
            message = "An unknown error occurred";
        }

        setError({ code: errorCode, message });
        setIsLoading(false);
      },
      {
        enableHighAccuracy,
        timeout,
        maximumAge,
      },
    );
  }, [isSupported, enableHighAccuracy, timeout, maximumAge]);

  const clear = useCallback(() => {
    setPosition(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Request location on mount if enabled
  useEffect(() => {
    if (enableOnMount && isSupported) {
      requestLocation();
    }
  }, [enableOnMount, isSupported, requestLocation]);

  return {
    position,
    error,
    isLoading,
    permissionState,
    requestLocation,
    clear,
    isSupported,
  };
}
